import { Actie, useGetDossierActiesById } from "@/entities/actie";
import { Maatregel, usePutDossierVsmById, Vsm } from "@/entities/vsm";
import {
    Button,
    Dialog,
    DialogBody,
    DialogContainer,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    ErrorDisplay,
    Icon,
    MessageBar,
} from "@/shared/components";
import { RadixBaseUiInteropContextProvider } from "@/shared/components/utils/radix-base-ui-interop-provider";
import { Dispatch, SetStateAction, useRef, useState } from "react";
import { useVsmEditorContext, useVsmEditorMaatregelen } from "../vsm-editor-context";
import { EditableVsm } from "./editable-vsm";

type NewEditVsmDialogProps = {
    dossierId: string;
    vsm: Vsm | null;
    open: boolean;
    onOpenChange: Dispatch<SetStateAction<boolean>>;
};

export function NewEditVsmDialog({ dossierId, open, onOpenChange, vsm }: NewEditVsmDialogProps) {
    const radixRef = useRef<HTMLDivElement | null>(null);
    const { data: vsmActie } = useGetDossierActiesById(dossierId, {
        select: (acties) => acties.find((actie) => Actie.isVsmActie(actie)),
    });
    const vsmMutation = usePutDossierVsmById();
    const maatregelen = useVsmEditorMaatregelen();
    const { status: vsmStatus, toelichting } = useVsmEditorContext();

    const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

    const maatregelenWithoutRijdtgroep = maatregelen.filter((m) => m.type !== "RijdtGroep");
    const hasMaatregelen = maatregelenWithoutRijdtgroep.length > 0;

    function handleSaveVsm() {
        if (!hasMaatregelen) {
            setHasAttemptedSubmit(true);
            return;
        }

        vsmMutation.mutate(
            {
                dossierId,
                maatregelen: maatregelenWithoutRijdtgroep.map((m) => Maatregel.toDto(m)),
                vsmStatus,
                toelichting,
            },
            { onSuccess: () => onOpenChange(false) }
        );
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <RadixBaseUiInteropContextProvider radixElement={radixRef}>
                <DialogContainer
                    className="md:min-w-184"
                    ref={radixRef}
                    onInteractOutside={(e) => {
                        // Allow clicking on toasts without closing dialog
                        const target = e.target as HTMLElement;
                        if (target.closest("[data-sonner-toast]")) {
                            e.preventDefault();
                        }
                    }}
                >
                    <DialogHeader>
                        {vsm && <DialogTitle>Versperringsmaatregel aanpassen</DialogTitle>}
                        {!vsm && <DialogTitle>Nieuwe versperringsmaatregel toevoegen</DialogTitle>}
                    </DialogHeader>
                    <DialogBody className="text-content-emphasis group grid gap-4" data-variant="nieuw">
                        {vsmActie && (
                            <MessageBar className="text-content-default">
                                Er is een nieuwe VSM beschikbaar vanuit Spoorweb. Sla je wijzigingen op of annuleer eerst, daarna kun je de
                                actie bekijken.
                            </MessageBar>
                        )}
                        <EditableVsm />

                        {hasAttemptedSubmit && !hasMaatregelen && (
                            <p className={"text-destructive text-s mb-1 flex items-center gap-2"} data-testid="error-missende-maatregelen">
                                <Icon name="error-filled" size="xs" />
                                Voeg minstens 1 maatregel toe
                            </p>
                        )}
                        {vsmMutation.error && <ErrorDisplay error={vsmMutation.error} />}
                    </DialogBody>
                    <DialogFooter>
                        <Button variant="tertiary" onClick={() => onOpenChange(false)}>
                            Annuleren
                        </Button>
                        <Button variant="primary" onClick={handleSaveVsm} disabled={vsmMutation.isPending}>
                            {vsm ? "Opslaan" : "Toevoegen aan dossier"}
                        </Button>
                    </DialogFooter>
                </DialogContainer>
            </RadixBaseUiInteropContextProvider>
        </Dialog>
    );
}
